import React from "react";
import { isEqual, isEmpty } from "../../utils/lodash";
import { QuestionModel } from "../../models/QuestionModel";
import { MentionModel } from "../../models/MentionModel";
import { MentionBuilderDelegate } from "../../models/builders/MentionBuilder";
import { QuestionType } from "../../models/enums/QuestionType";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import {
    getSettingBoolean,
    getSettingString,
    getSettingNumber,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { ThemeModel } from "../../models/ThemeModel";
import { Signal } from "../../signals";
import { SignalListener } from "../shared/SignalListener";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { isNullOrEmpty } from "../../utils";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { Voxco } from "../../public";
import {
    isAutoAdvanceEnabled,
    isInputRequired,
} from "../../models/utilities/questionnaireUtilities";
import {
    getSoftPromptCode,
    isAutoAdvanceCompatible,
    isQuestionSoftPrompt,
    shouldSetSoftPromptCode,
    shouldSetDefaultChoice,
} from "../../models/utilities/questionUtilities";
import { NavigationType } from "../../models/enums/NavigationType";
import { hasOpenEnd } from "../../models/utilities/choiceUtilities";
import { ElementWithSettings } from "../../models/ElementWithSettings";
import { VariableModel } from "../../models/VariableModel";
import { AxisModel } from "../../models/AxisModel";
import { BlockModel } from "../../models/BlockModel";
import { translate } from "../../models/utilities/translationUtilities";
import { FileUploadModel } from "../../models/FileUploadModel";
import {
    setMultipleVariablesMentions,
    setQuestionFieldStatus,
    setVariableMentions,
} from "../../redux/actions";
import { ChoiceModel } from "../../models/ChoiceModel";
// eslint-disable-next-line import/no-deprecated
import { supportValidateSoftPrompt } from "../../redux/thunks/validation";
import { __getStore__ } from "../../redux/store";
import { ThunkDispatchProp } from "../../models/actions/ThunkDispatchProp";
import { VariableType } from "../../models/enums/VariableType";
import { getAllCurrentPageQuestions } from "../../models/utilities/blockUtilities";

export interface ControlProps {
    // Store state
    block: BlockModel;
    questionnaire: QuestionnaireModel;
    files: { [variableName: string]: FileUploadModel[] };
    language: string;
    email: string;
    phone: string;
    timeZone: string;
    question: QuestionModel;
    theme: ThemeModel;
    mentions: { [variableName: string]: MentionModel[] };
    matrix: number;
    sequencedVariables: VariableModel[];
    sequencedRows: AxisModel[];
    sequencedColumns: AxisModel[];
    min: string;
    max: string;
    // Signals
    validateSignal?: Signal<ValidationOptionsModel, ValidationResultModel>;
    // Props
    isSingleOnPage?: boolean;
    showSoftPrompt: boolean;
    gridRendering?: boolean;
    prefix?: string;
    onNavigation?(type: NavigationType): void;
    isDisabled?: boolean;
    useClientValidation?: boolean;
    carrouselActiveVariableIndex?: number;
    onAfterSelection?(): void;
    executeUpdateForIdAfterSelection?: string;
}

export abstract class BaseControl<
    P extends ControlProps & ThunkDispatchProp,
    S
> extends React.Component<P, S> {
    renderedQuestion: QuestionModel;
    autoAdvanceTimer: number;
    preventAutoAdvance: boolean;

    UNSAFE_componentWillMount() {
        const { question } = this.props;
        this.initializeQuestion(question, this.props);
    }

    componentDidMount() {
        const { question } = this.props;
        if (question !== this.renderedQuestion) {
            this.questionFirstRender((this.renderedQuestion = question), this.props);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: P) {
        const { mentions } = this.props;
        const mentionChanged = !isEqual(mentions, nextProps.mentions);
        if (mentionChanged) {
            this.willUpdateMentions(nextProps.question, nextProps);
        }
    }

    componentDidUpdate(prevProps: P, prevState: S) {
        const { question, matrix, mentions, sequencedVariables } = this.props;
        const sameQuestion =
            question?.name === prevProps.question?.name && matrix === prevProps.matrix;
        const mentionChanged = !isEqual(mentions, prevProps.mentions);
        if (sameQuestion && mentionChanged) {
            this.didUpdateMentions(prevProps.question, prevProps);
        }
        if (question !== this.renderedQuestion) {
            this.questionFirstRender((this.renderedQuestion = question), this.props);
        }
        if (!isEqual(sequencedVariables, prevProps.sequencedVariables)) {
            this.didUpdateVariables(sequencedVariables, prevProps);
        }
    }

    componentWillUnmount() {
        const { question } = this.props;
        this.finalizeQuestion(question, this.props);
    }

    setMentions(
        props: P,
        variableName: string,
        builder: MentionBuilderDelegate,
        matrixIndex?: number,
        preventAutoAdvance?: boolean,
    ) {
        const { dispatch } = this.props;
        this.preventAutoAdvance = preventAutoAdvance;
        const index = matrixIndex || props.matrix;
        dispatch(setVariableMentions(variableName, builder, index));
    }

    setCarrouselActiveVariable = (questionId: number) => {
        const { sequencedVariables } = this.props;
        const carrouselActiveVariableIndex = __getStore__().getState().interview.carrousel[
            `${questionId}_statement`
        ]?.activeVariableIndex;
        return (
            carrouselActiveVariableIndex !== undefined &&
            sequencedVariables[carrouselActiveVariableIndex]
        );
    };

    getLatestMentionsForVariableName = (variableName: string) => {
        return __getStore__().getState().interview.variables.mentionsByVariableName[variableName];
    };

    updateMentionsAfterDisablingFields = (variableNames: any) => {
        const { dispatch } = this.props;
        dispatch(setMultipleVariablesMentions(variableNames));
    };

    updateQuestionFieldDisabledStatus = (ids: any, disabled: boolean) => {
        const { dispatch } = this.props;
        dispatch(setQuestionFieldStatus(ids, disabled));
    };

    disableAndResetHybridQuestionMapppedCell = (currentFieldIdentifier: string) => {
        const { question } = this.props;
        if (
            question?.parent?.type === QuestionType.HybridGrid &&
            !isNullOrEmpty(currentFieldIdentifier)
        ) {
            const [questionId, currentFieldVariableName] = currentFieldIdentifier.split("&");
            const disabledFieldKeys = [];
            const enableFieldKeys = [];
            const disableColumnsList = [];

            // To reset multiple mentions at once
            let mentionsByVariable: { [variableName: string]: MentionBuilderDelegate } = {};
            const addMention = (variableName: string, builder: MentionBuilderDelegate) => {
                mentionsByVariable = {
                    ...mentionsByVariable,
                    [variableName]: builder,
                };
            };

            const rows = question?.parent?.rows;
            const filteredRows = rows.filter(
                (x) =>
                    x.variables &&
                    x.variables.length > 0 &&
                    x.variables.find((vb) => vb.name === currentFieldVariableName),
            );

            if (filteredRows && filteredRows.length > 0) {
                // Get columns to be disabled for entire row if there is any mention selected for it.
                filteredRows[0].variables.forEach((loopVariable) => {
                    if (loopVariable.choiceList?.length > 0) {
                        const mention = this.getLatestMentionsForVariableName(loopVariable.name);

                        if (mention && mention.length > 0) {
                            loopVariable.choiceList.forEach((choice) => {
                                if (mention && mention.some((x) => x.value === choice.value)) {
                                    disableColumnsList.push(
                                        getSettingString(choice, "DisableColumns"),
                                    );
                                }
                            });
                        }
                    }
                });

                // Don't add filter for current variablename as index will vary
                filteredRows[0].variables.forEach((loopVariable, index) => {
                    const mappedFieldIdentifier = `${questionId}&${loopVariable.name}`;

                    // enableFieldKeys store all keys which will enable all column and disabledFieldKeys will disable based on choice selection again.
                    if (disableColumnsList && disableColumnsList.length > 0) {
                        enableFieldKeys.push(mappedFieldIdentifier);

                        const isDisabled = disableColumnsList
                            .join(",")
                            .replace(/\s/g, "")
                            .split(",")
                            .some((c) => c === index.toString());

                        if (isDisabled && currentFieldIdentifier !== mappedFieldIdentifier) {
                            disabledFieldKeys.push(mappedFieldIdentifier);

                            // Check latest mentions and reset only if there is any
                            const currentMentions = this.getLatestMentionsForVariableName(
                                loopVariable.name,
                            );

                            if (currentMentions && currentMentions.length > 0) {
                                addMention(loopVariable.name, (m) => m.clear());
                            }
                        }
                    } else {
                        enableFieldKeys.push(mappedFieldIdentifier);
                    }
                });
            }

            if (!isEmpty(enableFieldKeys)) {
                this.updateQuestionFieldDisabledStatus(enableFieldKeys, false);
            }

            if (!isEmpty(disabledFieldKeys)) {
                this.updateQuestionFieldDisabledStatus(disabledFieldKeys, true);
            }

            if (!isEmpty(mentionsByVariable)) {
                this.updateMentionsAfterDisablingFields(mentionsByVariable);
            }
        }
    };

    getMentions = (props: P, variableName?: string) => {
        const variable =
            this.setCarrouselActiveVariable(props.question.id) ||
            this.getQuestionVariable(props.question.variables);

        const mentions = props.mentions[(variableName || variable.name).toUpperCase()] || [];
        return mentions.filter((mention) => mention.matrix === props.matrix);
    };

    getMention = (props: P, variableName?: string, mentionIndex?: number) =>
        this.getMentions(props, variableName).find((m) => m.index === (mentionIndex || 1));

    getMentionValue = (props: P, variableName?: string, mentionIndex?: number) =>
        (this.getMention(props, variableName, mentionIndex) || { value: "" }).value;

    getMentionOpenEnd = (props: P, variableName?: string, mentionIndex?: number) =>
        (this.getMention(props, variableName, mentionIndex) || { openEnd: "" }).openEnd;

    axisHasMentions = (axis: AxisModel) => {
        return axis.variables.some(
            (variable) => !isEmpty(this.getMentions(this.props, variable.name)),
        );
    };

    getFiles = (props: P, variableName?: string) => {
        const variable = this.getQuestionVariable(props.question.variables);
        const files = props.files[(variableName || variable.name).toUpperCase()] || [];
        return files.filter((file) => file.matrix === props.matrix);
    };

    getFile = (props: P, variableName?: string) => {
        const [file] = this.getFiles(props, variableName);
        return file;
    };

    isSoftPrompt = (props: P) => {
        const { questionnaire, question } = props;
        return isInputRequired(questionnaire) && isQuestionSoftPrompt(question);
    };

    onAutoAdvanceTimer = () => {
        const { onNavigation } = this.props;
        this.autoAdvanceTimer = null;
        onNavigation(NavigationType.Next);
    };

    truncateOpenEnd = (element: ElementWithSettings, text: string): string => {
        const maxLenght = getSettingNumber(element, "MaxOpenEndLength", -1);
        if (maxLenght > 0 && text.length > maxLenght) {
            return text.substring(0, maxLenght);
        }
        return text;
    };

    parentCustomError = (parent: QuestionModel) => {
        const { language } = this.props;
        if (isEmpty(parent)) {
            return null;
        }
        if (parent.type === QuestionType.StarRatingGrid) {
            return translate(parent.columns[0], "CustomErrorMessage", language);
        }
        return translate(parent, "CustomErrorMessage", language);
    };

    validateHandler = (options?: ValidationOptionsModel) => {
        if (!this.shouldValidate()) {
            return new ValidationBuilder().build();
        }
        return this.validate(options);
    };

    isOpenEndRequired = (element: ElementWithSettings): boolean =>
        hasSetting(element, "EnforceOpenEnd") ? getSettingBoolean(element, "EnforceOpenEnd") : true;

    isAxisAnswerRequired = (axis: AxisModel) =>
        !hasSetting(axis, "AnswerRequired") || getSettingBoolean(axis, "AnswerRequired");

    isChoiceVisible = (choiceValue: string, props: P, variable = props.sequencedVariables?.[0]) =>
        this.hasChoiceWithValue(choiceValue, variable);

    isChoiceNoLongerVisible = (choiceValue: string, prevProps: P) =>
        this.isChoiceVisible(choiceValue, prevProps) &&
        !this.isChoiceVisible(choiceValue, this.props);

    isValidChoice = (value: string) => {
        const { question } = this.props;
        const variable = this.getQuestionVariable(question.variables);
        return this.hasChoiceWithValue(value, variable);
    };

    hasChoiceWithValue = (value: string, variable: VariableModel) =>
        variable.choiceList.some((choice) => choice.value === value);

    removeUnavailableMentions = (variable: VariableModel) => {
        const mentions = this.getMentions(this.props, variable.name);
        mentions.forEach((mention) => {
            const shouldRemoveMention = !this.shouldDisplayMentionValue(variable, mention.value);
            if (shouldRemoveMention) {
                this.setMentions(this.props, variable.name, (m) => m.removeValue(mention.value));
            }
        });
    };

    getQuestionVariable = (variables: VariableModel[]) =>
        variables.find((v) => v.type !== VariableType.Computed);

    shouldDisplayMentionValue = (variable: VariableModel, value: string) => {
        const { choiceList = [] } = variable;
        const { question, sequencedVariables } = this.props;
        if (choiceList.length > 0) {
            const defaultChoices = choiceList.filter(
                (choice) =>
                    getSettingBoolean(choice, "Default") && shouldSetDefaultChoice(question),
            );
            const softPromptCode = shouldSetSoftPromptCode(question)
                ? getSoftPromptCode(question)
                : null;
            const isChoiceDefault = defaultChoices.some((choice) => choice.value === value);
            const sequencedVariable = sequencedVariables.find((v) => v.name === variable.name);
            const isChoiceVisible = this.isChoiceVisible(value, this.props, sequencedVariable);
            const isChoiceSoftPrompt = value === softPromptCode;
            return isChoiceVisible || (!isChoiceDefault && !isChoiceSoftPrompt);
        }
        return (
            this.isVariableComputed(question, variable) ||
            sequencedVariables?.find((v) => v.name === variable.name)
        );
    };

    isVariableComputed = (question: QuestionModel, variable: VariableModel) =>
        question.variables?.find(
            (v) => v.name === variable.name && v.type === VariableType.Computed,
        );

    setChoicesAsMentions = (element: ElementWithSettings, choices: ChoiceModel[]) => {
        const values = choices.slice(0, this.maxValue() || choices.length).map((choice) => ({
            value: choice.value,
        }));
        if (values.length) {
            this.setMentions(
                this.props,
                element.name,
                (mentions) => mentions.clear().add(values),
                undefined,
                true,
            );
        }
    };

    isAnswerRequired(questionnaire: QuestionnaireModel, question: QuestionModel): boolean {
        if (question.type === QuestionType.Description) {
            return false;
        }
        if (!isInputRequired(questionnaire)) {
            return false;
        }
        const answerRequired =
            !hasSetting(question, "AnswerRequired") ||
            getSettingBoolean(question, "AnswerRequired");
        const control = !answerRequired ? question : question.parent || question;
        return (
            !hasSetting(control, "AnswerRequired") ||
            getSettingBoolean(control, "AnswerRequired", "Yes", "SoftPrompt")
        );
    }

    /**
     * This is called when the question will disappear; either it is
     * hidden by a skip logic or the page has changed.
     * Use this to unsubscribe to events, dispose of resources
     * or save mentions in some cases.
     * @param question
     * @param props
     */
    finalizeQuestion(question: QuestionModel, props: P) {}

    minValue() {
        const { min } = this.props;
        const value = parseInt(min, 10);
        return !Number.isNaN(value) ? value : null;
    }

    maxValue() {
        const { max } = this.props;
        const value = parseInt(max, 10);
        return !Number.isNaN(value) ? value : null;
    }

    hasMentions(props: P, variableName?: string) {
        const mentions = this.getMentions(props, variableName || props.sequencedVariables[0].name);
        return mentions.some((mention) => this.mentionIsValid(mention, props.question));
    }

    hasOpenEnd(question: QuestionModel) {
        const { mentions } = this.props;
        const openEndChoices = question.variables[0].choiceList.filter(hasOpenEnd);
        const mentionValues = Object.values(mentions).reduce((acc, val) => acc.concat(val), []);
        return mentionValues.some((mention) =>
            openEndChoices.some((openEndChoice) => mention.value === openEndChoice.value),
        );
    }

    isAnswerComplete(props: P) {
        return this.hasMentions(props);
    }

    mentionIsValid(mention: MentionModel, question: QuestionModel) {
        return mention.value !== undefined && mention.value !== null && mention.value.length > 0;
    }

    shouldValidate(): boolean {
        const { isDisabled } = this.props;
        return !isDisabled;
    }

    /**
     * This is called after the question is first displayed. It will be called only
     * once. Use this instead of UNSAFE_componentWillMount.
     * @param question
     * @param props
     */
    questionFirstRender(question: QuestionModel, props: P) {}

    /**
     * This is called before the question is first displayed. It will be called only
     * once. Use this instead of UNSAFE_componentWillMount.
     * @param question
     * @param props
     */
    initializeQuestion(question: QuestionModel, props: P) {
        const { dispatch } = this.props;
        const [firstVariable] = question.variables;
        const defaultValue = getSettingString(props.question, "DefaultValue");
        const hasDefaultValue = defaultValue !== undefined && defaultValue.length > 0;
        if (hasDefaultValue && !this.hasMentions(props)) {
            dispatch(
                setVariableMentions(firstVariable.name, (mentions) => mentions.set(defaultValue)),
            );
        }
        question.variables.forEach(this.removeUnavailableMentions);
    }

    /**
     * This is called before mentions are updated.
     * @param question
     * @param nextProps
     */
    willUpdateMentions(question: QuestionModel, nextProps: P) {}

    /**
     * This is called after mentions are updated.
     * @param question
     * @param nextProps
     */
    didUpdateMentions(question: QuestionModel, prevProps: P) {
        const { mentions } = this.props;
        const event = Voxco.trigger("controlDidUpdateMentions", {
            question,
            mentions,
        });

        if (!event.defaultPrevented) {
            if (this.autoAdvanceTimer) {
                window.clearInterval(this.autoAdvanceTimer);
            }

            const firstVariable = question.variableNames[0];
            const firstVariableMentionChanged =
                prevProps.mentions[firstVariable] !== mentions[firstVariable];

            const shouldSetAutoAdvanceTimer =
                (firstVariableMentionChanged ||
                    this.anyMentionChangedForQuestion(question, prevProps)) &&
                this.shouldAutoAdvance() &&
                !this.hasOpenEnd(question) &&
                Voxco.validatePage({ silent: true }).isValid;

            if (shouldSetAutoAdvanceTimer) {
                this.autoAdvanceTimer = window.setTimeout(this.onAutoAdvanceTimer, 300);
            }
        }
    }

    anyMentionChangedForQuestion(question: QuestionModel, prevProps: P) {
        const { mentions } = this.props;
        switch (question.type) {
            case QuestionType.Carrousel:
                return prevProps.mentions !== mentions;
            default:
                return false;
        }
    }

    didUpdateVariables(sequencedVariables: VariableModel[], prevProps: P) {}

    failValidation(variables: VariableModel[], key: string, args?: { [name: string]: string }) {
        const validationBuilder = new ValidationBuilder();
        variables.forEach((variable) => validationBuilder.fail(variable.name, key, args));
        return validationBuilder.build();
    }

    validateSoftPrompt(props: P, variables?: VariableModel[]): ValidationResultModel {
        const { question, useClientValidation, dispatch } = props;
        if (!useClientValidation) {
            return new ValidationBuilder().build();
        }
        const variablesToValidate = props.sequencedVariables?.filter(
            (v) => v.type !== VariableType.Computed,
        );
        // eslint-disable-next-line import/no-deprecated
        return supportValidateSoftPrompt(
            question,
            variablesToValidate,
            dispatch,
            __getStore__().getState(),
        );
    }

    validateOpenEnd(
        openEnd: string,
        element: ElementWithSettings,
        variableName?: string,
    ): ValidationResultModel {
        const { question } = this.props;
        const [variable] = question.variables;
        const isOpenEndQuestion = question.type === QuestionType.OpenEndAnswer;
        const value = isNullOrEmpty(openEnd) ? openEnd : openEnd.trim();

        const customError = getSettingString(question, "CustomErrorMessage");
        const minLength = isOpenEndQuestion
            ? getSettingNumber(element, "MinAnswerLength", 0)
            : getSettingNumber(element, "MinOpenEndLength", 0);

        if (!isOpenEndQuestion && this.isOpenEndRequired(element) && isNullOrEmpty(value)) {
            return new ValidationBuilder()
                .fail(variableName || variable.name, customError || "IncompleteAnswer", {
                    openEnd: element.name,
                })
                .build();
        }

        if (!isNullOrEmpty(value) && value.length < minLength) {
            return new ValidationBuilder()
                .fail(variableName || variable.name, customError || "InvalidCharactersMin", {
                    min: String(minLength),
                    openEnd: element.name,
                })
                .build();
        }

        return new ValidationBuilder().build();
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { questionnaire, question, sequencedVariables } = this.props;
        const required = this.isAnswerRequired(questionnaire, question);
        const hasMentions = this.isAnswerComplete(this.props);
        if (!hasMentions && required) {
            const isSoftPrompt = this.isSoftPrompt(this.props);
            if (isSoftPrompt) {
                return this.validateSoftPrompt(this.props);
            }
            // Question is required but has no mentions.
            const customError =
                getSettingString(question, "CustomErrorMessage") ||
                this.parentCustomError(question.parent);
            return new ValidationBuilder()
                .fail(sequencedVariables[0].name, customError || "IncompleteAnswer")
                .build();
        }
        // Question is valid.
        return new ValidationBuilder().build();
    }

    shouldAutoAdvance() {
        const { questionnaire, block, question, isSingleOnPage } = this.props;
        const pageQuestions = getAllCurrentPageQuestions(block, question.index);
        const variableDependencies = pageQuestions.flatMap((q) => q.variableDependencies);
        const variables = pageQuestions.flatMap((q) => q.variableNames);
        const pageHasDynamicLogic = variableDependencies.some((v) => variables.includes(v));

        if (pageHasDynamicLogic) {
            return false;
        }
        if (!isAutoAdvanceEnabled(questionnaire, block)) {
            return false;
        }
        if (this.preventAutoAdvance) {
            this.preventAutoAdvance = null;
            return false;
        }
        if (!isSingleOnPage) {
            return false;
        }
        return isAutoAdvanceCompatible(question, this.props);
    }

    /**
     * Override this to render the question element.
     * @param question
     */
    abstract renderQuestion(question: QuestionModel): JSX.Element | null;

    render() {
        const { question, validateSignal } = this.props;
        return (
            <>
                <SignalListener signal={validateSignal} handler={this.validateHandler} />
                {this.renderQuestion(question)}
            </>
        );
    }
}
